import type { Zero } from "@rocicorp/zero";
import type { Schema } from "../lib/zero-schema.js";
import { mutators } from "../lib/mutators.js";
import { fetchLinkPreviewRequest } from "@ano/shared/api/link-preview";
import { API_URL } from "../lib/config.js";

export interface LinkPreviewMetadata {
  url: string;
  title: string | null;
  description: string | null;
  image_url: string | null;
  favicon_url: string | null;
  site_name: string | null;
}

/**
 * Fetch link preview metadata from the server for a single URL.
 * Returns null if the fetch fails.
 */
async function fetchLinkPreview(
  url: string,
  token: string,
): Promise<LinkPreviewMetadata | null> {
  try {
    return await fetchLinkPreviewRequest({
      apiBaseUrl: API_URL,
      accessToken: token,
      url,
    });
  } catch {
    return null;
  }
}

/**
 * Fetch and store link previews for all URLs in a message.
 * Runs in the background - failures are silently ignored.
 */
export async function fetchAndStoreLinkPreviews(
  zero: Zero<Schema>,
  messageId: string,
  urls: string[],
  token: string,
): Promise<void> {
  const uniqueUrls = [...new Set(urls)];

  await Promise.all(
    uniqueUrls.map(async (url) => {
      const metadata = await fetchLinkPreview(url, token);
      if (!metadata) return;

      const now = Date.now();
      const result = zero.mutate(
        mutators.link_previews.insert({
          id: crypto.randomUUID(),
          message_id: messageId,
          url: metadata.url,
          title: metadata.title,
          description: metadata.description,
          image_url: metadata.image_url,
          favicon_url: metadata.favicon_url,
          site_name: metadata.site_name,
          fetched_at: now,
          created_at: now,
        }),
      );
      await result.client.catch(() => undefined);
    }),
  );
}
